import React, { useEffect, useState } from 'react';
import Styled from 'styled-components';

import {
  Card,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Close';
import TuneIcon from '@material-ui/icons/Tune';

import CardTitle from '../../../common/card/CardTitle';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import CreateEditProductComponentDialog from '../dialogs/CreateEditProductComponentDialog';
import {
  useDeleteProductComponent,
  useFindProductComponents
} from '../Product.hooks';
import { Product, ProductComponent } from '../Product.interface';
import { AppSnackbar } from '@sierralabs/react-material-ui';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';

const StyledAction = Styled.div`
  flex: 1;
  text-align: right;
`;

const ProductComponentCard: React.FC<{ product: Product }> = ({ product }) => {
  const [
    openCreateEditDialog,
    setOpenCreateEditDialog
  ] = useState<ProductComponent | null>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<ProductComponent>();
  const {
    productComponents,
    isLoading,
    loadingError,
    findProductComponents
  } = useFindProductComponents(product.id, true);
  const {
    deletedComponent,
    deletingComponent,
    deleteComponentError,
    deleteComponent
  } = useDeleteProductComponent(product.id, openDeleteDialog?.id || 0);
  useEffect(() => {
    if (deletedComponent) {
      findProductComponents();
    }
  }, [deletedComponent, findProductComponents]);
  const { isFieldDisabled } = useTaskEntityFields(product.task);
  return (
    <Card>
      <CardTitle>
        <TuneIcon />
        Manage Components
        <StyledAction>
          <IconButton
            size='small'
            disabled={isFieldDisabled('components')}
            onClick={() => setOpenCreateEditDialog(null)}
          >
            <AddIcon />
          </IconButton>
        </StyledAction>
      </CardTitle>
      <TableContainer>
        <Table>
          <TableBody>
            {(isLoading || deletingComponent) && (
              <TableRow>
                <TableCell colSpan={2} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productComponents?.map(component => (
              <TableRow
                hover={!isFieldDisabled('components')}
                key={component.id}
                onClick={() => {
                  if (!isFieldDisabled('components')) {
                    setOpenCreateEditDialog(component);
                  }
                }}
              >
                <TableCell>{component.name}</TableCell>
                <TableCell align='right'>
                  <IconButton
                    size='small'
                    disabled={isFieldDisabled('components')}
                    onClick={event => {
                      event.stopPropagation();
                      setOpenDeleteDialog(component);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {productComponents?.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>No Product Components</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {(openCreateEditDialog || openCreateEditDialog === null) && (
        <CreateEditProductComponentDialog
          open
          productId={product.id}
          productComponent={openCreateEditDialog}
          onCancel={() => {
            setOpenCreateEditDialog(undefined);
          }}
          onSubmit={() => {
            setOpenCreateEditDialog(undefined);
            findProductComponents(); // refresh data
          }}
        />
      )}
      {loadingError && (
        <AppSnackbar severity='error' open>
          Error loading component: {loadingError.message}
        </AppSnackbar>
      )}
      {deleteComponentError && (
        <AppSnackbar severity='error' open>
          Error deleting component: {deleteComponentError.message}
        </AppSnackbar>
      )}
      {openDeleteDialog && (
        <ConfirmDialog
          open
          title='Delete Confirm'
          message={`Are you sure you want to remove '${openDeleteDialog?.name}' from the product component?`}
          onCancel={() => {
            setOpenDeleteDialog(undefined);
          }}
          onConfirm={() => {
            setOpenDeleteDialog(undefined);
            deleteComponent();
          }}
        />
      )}
    </Card>
  );
};

export default ProductComponentCard;
